import { useState } from "react";
import { toast } from "sonner";
import { Building, AlertOctagon, Mail } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const BranchRow = ({ b, onDone }) => {
  const { t } = useI18n();
  const [name, setName] = useState(b.name);
  const save = async () => {
    try { await api.put(`/branches/${b.id}`, { name }); toast.success(t("success")); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-2" data-testid={`branch-row-${b.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`branch-name-${b.id}`} />
      <Button size="sm" variant="outline" disabled={!name.trim() || name === b.name} onClick={save} data-testid={`branch-save-${b.id}`}>{t("save")}</Button>
    </div>
  );
};

export const BranchesAdmin = ({ branches, onDone }) => {
  const { t } = useI18n();
  return (
    <div className="bg-white border rounded-lg p-5 space-y-3 max-w-xl" data-testid="branches-admin">
      <h3 className="font-bold flex items-center gap-2"><Building className="w-4 h-4" />{t("branches")}</h3>
      {branches.map((b) => <BranchRow key={b.id + b.name} b={b} onDone={onDone} />)}
    </div>
  );
};

export const ChangeEmailCard = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ new_email: "", current_password: "" });
  const save = async () => {
    try { await api.post("/auth/change-email", form); toast.success(t("emailChanged")); setTimeout(logout, 1500); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-lg p-5 space-y-3 max-w-xl" data-testid="change-email-card">
      <h3 className="font-bold flex items-center gap-2"><Mail className="w-4 h-4" />{t("changeEmail")}</h3>
      <p className="text-sm text-slate-600" dir="ltr" data-testid="current-admin-email">{user?.email}</p>
      <Input dir="ltr" type="email" placeholder={t("newEmail")} value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })} data-testid="change-email-input" />
      <Input type="password" placeholder={t("currentPassword")} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="change-email-password-input" />
      <Button disabled={!form.new_email.trim() || !form.current_password} onClick={save} data-testid="change-email-save-btn">{t("save")}</Button>
    </div>
  );
};

export const ResetDataCard = () => {
  const { t } = useI18n();
  const { logout } = useAuth();
  const [text, setText] = useState("");
  const reset = async () => {
    if (!window.confirm(t("resetConfirm"))) return;
    try { await api.post("/admin/reset", { confirm: text }); toast.success(t("resetDone")); localStorage.removeItem("branch"); setTimeout(logout, 1200); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border-2 border-rose-300 bg-rose-50 rounded-lg p-5 space-y-3 max-w-xl" data-testid="reset-data-card">
      <h3 className="font-bold flex items-center gap-2 text-rose-800"><AlertOctagon className="w-4 h-4" />{t("resetData")}</h3>
      <p className="text-sm text-rose-900">{t("resetHint")}</p>
      <div className="flex gap-2">
        <Input dir="ltr" placeholder="RESET" value={text} onChange={(e) => setText(e.target.value)} className="bg-white" data-testid="reset-confirm-input" />
        <Button disabled={text !== "RESET"} className="bg-rose-600 hover:bg-rose-700 text-white" onClick={reset} data-testid="reset-data-btn">{t("resetData")}</Button>
      </div>
    </div>
  );
};
